import * as React from "react"
import { Link } from "gatsby"

import { MdArrowDropDown, MdDone, MdAdd, MdBlock } from "react-icons/md"
import {
  header as text,
  organizationPlan as organizationPlanText,
} from "@modules/locales/default.js"
import {
  DropdownMenu,
  DropdownMenuButton,
  DropdownMenuItems,
  DropdownMenuLink,
  Theme,
  ThemeCss,
} from "gatsby-interface"
import {
  useTracker,
  GAEventType,
  BigQueryEventType,
  SegmentEventType,
} from "@modules/analytics"
import Breadcrumb from "./Breadcrumb"
import { getPathToOrgSites } from "@modules/organization/shared/utils"
import { SubscriptionStatus } from "@modules/graphql/types"
import { UserOrganizationsQuery } from "@modules/header/queries.generated"
import { idRegex } from "@modules/toolkit/constants"
import { interpolateMessage } from "@modules/locales"
import { visuallyHiddenCss } from "@modules/a11y/stylesheets"

const menuButtonCss: ThemeCss = theme => ({
  padding: 0,
  alignItems: "center",
  background: `none`,
  border: `0`,
  color: theme.colors.grey[90],
  cursor: `pointer`,
  display: `flex`,
  fontFamily: theme.fonts.heading,
  fontSize: theme.fontSizes[3],
  fontWeight: theme.fontWeights.bold,
  whiteSpace: `nowrap`,
  position: `relative`,
  justifySelf: `center`,
  justifyContent: `center`,
})

const menuButtonIconCss: ThemeCss = theme => ({
  flexShrink: 0,
  fontSize: theme.space[5],
  marginLeft: theme.space[3],
})

const menuListCss: ThemeCss = theme => ({
  position: "absolute",
  zIndex: 20,
  [theme.mediaQueries.desktop]: {
    maxWidth: `15rem`,
  },
})

const itemIconCss: ThemeCss = theme => ({
  color: theme.colors.purple[50],
  marginInlineStart: theme.space[5],
  flexShrink: 0,
  fontSize: `20px`,
})

const menuLinkCss = (theme: Theme, isBlocked = false) => [
  {
    display: `flex`,
    alignItems: `baseline`,
    justifyContent: `space-between`,
  },
  isBlocked && {
    opacity: 0.5,
    cursor: `not-allowed`,
  },
]

const planNameCss: ThemeCss = theme => ({
  display: `block`,
  fontSize: theme.fontSizes[0],
  lineHeight: theme.lineHeights.default,
  color: theme.colors.grey[60],
})

const createButtonCss: ThemeCss = theme => [
  menuLinkCss(theme),
  {
    borderTop: `1px solid ${theme.colors.grey[20]}`,
    color: theme.colors.gatsby,
    alignItems: `center`,
  },
]

export type UserOrganizationsDropdownProps = {
  userOrganizations: UserOrganizationsQuery["currentOrganizations"]
  selectedOrganizationId: string
  path: string
}

export function UserOrganizationsDropdown({
  userOrganizations,
  selectedOrganizationId,
  path,
}: UserOrganizationsDropdownProps) {
  const { trackDropdownButtonClick, trackOrganizationItemClick } =
    useUserOrgsDropdownAnalytics()

  if (!userOrganizations) {
    return null
  }

  const selectedOrganizationName = userOrganizations.find(
    ({ id }) => id === selectedOrganizationId
  )?.name

  if (
    (path.includes(`builds`) && !path.includes(`dashboard/builds`)) ||
    new RegExp(`sites/${idRegex}/deploys`).test(path) ||
    new RegExp(`sites/${idRegex}/settings`).test(path) ||
    new RegExp(`sites/${idRegex}/branches`).test(path) ||
    new RegExp(`sites/${idRegex}/cmsPreview`).test(path) ||
    new RegExp(`sites/${idRegex}/dataLayer`).test(path) ||
    new RegExp(`sites/${idRegex}/pullRequests`).test(path) ||
    new RegExp(`sites/${idRegex}/functions`).test(path)
  ) {
    return (
      <Breadcrumb
        orgName={selectedOrganizationName}
        orgId={selectedOrganizationId}
        path={path}
      />
    )
  }

  return (
    <DropdownMenu id="user-orgs-dropdown">
      <DropdownMenuButton
        css={menuButtonCss}
        onClick={() => {
          trackDropdownButtonClick()
        }}
        data-testid="user-orgs-dropdown-button"
      >
        {selectedOrganizationName
          ? selectedOrganizationName
          : text.allOrganizations}
        <MdArrowDropDown css={menuButtonIconCss} />
      </DropdownMenuButton>

      <DropdownMenuItems css={menuListCss} size="MAX_CONTENT">
        {userOrganizations.map(org => {
          const isBlocked = Boolean(org.blocked)
          const isActive = org.id === selectedOrganizationId
          const isTrialing =
            org?.billing?.status === SubscriptionStatus.Trialing
          const planName = org?.billing?.plan?.name || ""
          const planLabel = isTrialing
            ? interpolateMessage<"planName">(
                organizationPlanText.labels.triallingPlan,
                { planName }
              )
            : planName

          return (
            <DropdownMenuLink
              as={Link}
              onClick={() => {
                trackOrganizationItemClick(org.id, org.name)
              }}
              key={org.id}
              to={getPathToOrgSites(org.id)}
              aria-current={isActive ? "location" : undefined}
              css={theme => menuLinkCss(theme, isBlocked)}
            >
              <span>
                {org.name}
                {isActive && (
                  <span css={visuallyHiddenCss}>
                    {text.labels.currentOrgIndicator}
                  </span>
                )}
                <span
                  css={planNameCss}
                  aria-label={interpolateMessage<"planName">(
                    organizationPlanText.labels.currentPlanName,
                    {
                      planName: planLabel,
                    }
                  )}
                >
                  {planLabel}
                </span>
              </span>
              {isActive && !isBlocked && (
                <MdDone aria-hidden css={itemIconCss} />
              )}
              {isBlocked && (
                <MdBlock
                  aria-hidden
                  css={theme => [
                    itemIconCss(theme),
                    { color: theme.colors.red[60] },
                  ]}
                />
              )}
            </DropdownMenuLink>
          )
        })}
        <DropdownMenuLink
          key="new-space"
          as={Link}
          to={`/dashboard/organization/create`}
          css={createButtonCss}
        >
          {text.actions.createOrganization}
          <MdAdd css={itemIconCss} />
        </DropdownMenuLink>
      </DropdownMenuItems>
    </DropdownMenu>
  )
}

function useUserOrgsDropdownAnalytics() {
  const { trackGoogleAnalytics, trackBigQuery, trackSegment } = useTracker()

  const trackDropdownButtonClick = () => {
    trackBigQuery({
      eventType: BigQueryEventType.ButtonClicked,
      buttonName: `Organization Menu`,
      uiSource: `Navbar`,
    })
    trackGoogleAnalytics({
      eventType: GAEventType.Click,
      category: `Product Engagement - Dashboard`,
      label: { loc: `Header`, text: `all organizations dropdown` },
    })
  }

  const trackOrganizationItemClick = (orgId: string, orgName: string) => {
    trackBigQuery<{ organizationId: string }>({
      eventType: BigQueryEventType.ButtonClicked,
      buttonName: `Select Organization`,
      uiSource: `Navbar`,
      organizationId: orgId,
    })
    trackGoogleAnalytics({
      eventType: GAEventType.Click,
      category: `Product Engagement - Dashboard`,
      label: { loc: `Header`, text: `all organizations - go to org` },
    })
    trackSegment({
      type: SegmentEventType.Group,
      workspaceId: orgId,
      traits: {
        name: orgName,
      },
    })
  }

  return {
    trackDropdownButtonClick,
    trackOrganizationItemClick,
  }
}
